import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Send, Mail, ChevronRight } from 'lucide-react';
import Header from './Header';
import Footer from './Footer';
import SEO from './SEO';
import { benefits, jobs } from '../data/karriere';

function BenefitCard({ benefitItem }) {
  var Icon = benefitItem.Icon;
  return (
    <div data-testid={"benefit-" + benefitItem.id} className="bg-white border border-zinc-200 p-6 group hover:border-red-200 transition-all duration-300">
      <div className="w-12 h-12 bg-red-50 flex items-center justify-center mb-4 group-hover:bg-[#E31E24] transition-colors">
        <Icon className="w-6 h-6 text-[#E31E24] group-hover:text-white transition-colors" />
      </div>
      <h3 className="text-lg font-bold text-neutral-900 mb-2">{benefitItem.title}</h3>
      <p className="text-sm text-neutral-600 leading-relaxed">{benefitItem.description}</p>
    </div>
  );
}

function TaskList({ heading, items }) {
  if (!items || items.length === 0) return null;
  var listItems = [];
  for (var i = 0; i < items.length; i++) {
    listItems.push(
      <li key={i} className="flex items-start gap-2 text-sm text-neutral-600">
        <ChevronRight className="w-4 h-4 text-[#E31E24] mt-0.5 flex-shrink-0" />
        {items[i]}
      </li>
    );
  }
  return (
    <div className="mb-6">
      <h4 className="text-sm font-bold uppercase tracking-wider text-neutral-800 mb-3">{heading}</h4>
      <ul className="space-y-2">{listItems}</ul>
    </div>
  );
}

function JobCard({ jobItem }) {
  return (
    <div data-testid={"job-" + jobItem.id} className="bg-white border border-zinc-200 overflow-hidden">
      <div className="p-8">
        <div className="mb-4">
          <span className="inline-block bg-[#E31E24] text-white text-xs font-bold uppercase tracking-wider px-3 py-1 mb-3">{jobItem.type}</span>
          <h3 className="text-2xl font-bold text-neutral-900">{jobItem.title}</h3>
          <p className="text-[#E31E24] font-medium">{jobItem.subtitle}</p>
        </div>
        <p className="text-neutral-600 leading-relaxed mb-6">{jobItem.description}</p>
        <TaskList heading="Deine Aufgaben" items={jobItem.tasks} />
        <TaskList heading="Das bringst du mit" items={jobItem.requirements} />
        <a
          href={"mailto:karriere@fesch-elektrotechnik.de?subject=Bewerbung: " + jobItem.title}
          data-testid={"apply-" + jobItem.id}
          className="inline-flex items-center gap-2 bg-[#E31E24] text-white font-semibold px-6 py-3 hover:bg-[#c91a1f] transition-colors text-sm uppercase tracking-wider"
        >
          <Send className="w-4 h-4" />
          Jetzt bewerben
        </a>
      </div>
    </div>
  );
}

export default function KarrierePage() {
  useEffect(function() { window.scrollTo(0, 0); }, []);

  var benefitCards = [];
  for (var i = 0; i < benefits.length; i++) {
    benefitCards.push(<BenefitCard key={benefits[i].id} benefitItem={benefits[i]} />);
  }

  var jobCards = [];
  for (var j = 0; j < jobs.length; j++) {
    jobCards.push(<JobCard key={jobs[j].id} jobItem={jobs[j]} />);
  }

  return (
    <div className="min-h-screen" data-testid="karriere-page">
      <SEO page="karriere" />
      <Header />
      <main>
        <section className="bg-neutral-900 pt-28 pb-16 md:pt-36 md:pb-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Link to="/" data-testid="karriere-back-home" className="inline-flex items-center gap-2 text-white/60 hover:text-white text-sm font-medium mb-8 transition-colors">
              <ArrowLeft className="w-4 h-4" />
              {"Zur\u00fcck zur Startseite"}
            </Link>
            <span className="block text-[#E31E24] font-semibold text-sm uppercase tracking-widest mb-4">Karriere</span>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">Werde Teil unseres Teams</h1>
            <p className="text-white/60 text-base lg:text-lg max-w-2xl">
              {"Fesch Elektrotechnik \u2013 ein junges, modernes Team mit Leidenschaft f\u00fcr Elektrotechnik. Wir suchen Verst\u00e4rkung f\u00fcr spannende Projekte deutschlandweit."}
            </p>
          </div>
        </section>

        <section className="py-16 md:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <span className="inline-block text-[#E31E24] font-semibold text-sm uppercase tracking-widest mb-4">Warum Fesch?</span>
              <h2 className="text-3xl sm:text-4xl font-bold text-neutral-900 mb-4">Deine Vorteile bei uns</h2>
              <p className="text-neutral-600 max-w-2xl mx-auto">
                {"Bei Fesch Elektrotechnik erwartet dich mehr als nur ein Job \u2013 wir bieten dir ein Arbeitsumfeld, in dem du wachsen kannst."}
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {benefitCards}
            </div>
          </div>
        </section>

        <section className="py-16 md:py-24 bg-zinc-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <span className="inline-block text-[#E31E24] font-semibold text-sm uppercase tracking-widest mb-4">Offene Stellen</span>
              <h2 className="text-3xl sm:text-4xl font-bold text-neutral-900 mb-4">Aktuelle Stellenangebote</h2>
              <p className="text-neutral-600 max-w-2xl mx-auto">
                Finde die passende Position und starte deine Karriere bei Fesch Elektrotechnik.
              </p>
            </div>
            <div className="space-y-6 max-w-4xl mx-auto">
              {jobCards}
            </div>
          </div>
        </section>

        <section className="bg-neutral-900 py-16 md:py-20">
          <div className="max-w-3xl mx-auto px-4 text-center">
            <Mail className="w-12 h-12 text-[#E31E24] mx-auto mb-6" />
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">{"Bereit f\u00fcr den n\u00e4chsten Schritt?"}</h2>
            <p className="text-white/60 mb-4 max-w-xl mx-auto">
              {"Sende deine Bewerbung direkt an uns \u2013 wir freuen uns, von dir zu h\u00f6ren!"}
            </p>
            <a href="mailto:karriere@fesch-elektrotechnik.de" data-testid="karriere-email-cta" className="inline-block text-[#E31E24] text-xl sm:text-2xl font-bold mb-8 hover:underline">
              karriere@fesch-elektrotechnik.de
            </a>
            <div>
              <Link to="/" data-testid="karriere-back-startseite" className="btn-primary inline-block">
                {"Zur\u00fcck zur Startseite"}
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
